import type { ErrorEvent } from '@sentry/react';

// Benign browser transport/abort errors (offline, navigation away, CORS), anchored
// so we don't swallow real errors that merely contain the phrase. (ENG-2797)
const BENIGN_ERROR_PATTERNS: readonly RegExp[] = [
  /^Failed to fetch$/, // Chromium
  /^NetworkError when attempting to fetch resource\.?$/, // Firefox
  /^Load failed$/, // Safari / WebKit
  /^The network connection was lost\.?$/, // iOS WebKit
];

// Keep a small sample so a real backend outage still surfaces instead of going dark.
const BENIGN_SAMPLE_RATE = 0.05;

// Drop ~95% of matched benign events; the retained 5% keeps outages visible.
function sampledOut(): boolean {
  return Math.random() >= BENIGN_SAMPLE_RATE;
}

/**
 * Third-party script origins whose errors are not ours to action. Passed to
 * Sentry's `denyUrls` so errors thrown from these scripts are dropped regardless
 * of page or message. The Help Scout Beacon widget (loaded from
 * beacon-v2.helpscout.net) retry-storms and throws an unhandled AxiosError when
 * its API is unreachable (ad-blocker / network); that noise surfaced as
 * WEB-APP-RS, which has nothing to do with our backend. (ENG-2857)
 */
export const THIRD_PARTY_DENY_URLS: readonly RegExp[] = [/helpscout\.net/];

/**
 * Whether a Sentry event is benign noise to drop. Anchored message matches are
 * dropped ~95% of the time; the retained 5% keeps outages visible. A message
 * that only *contains* a pattern is never dropped.
 */
export function shouldDropBenignError(message: string | undefined): boolean {
  if (!message) {
    return false;
  }
  if (BENIGN_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return sampledOut();
  }
  return false;
}

/**
 * Sentry `beforeSend` that discards benign browser noise (returns null) and
 * keeps everything else. Shared by the web apps.
 */
export function dropBenignError(event: ErrorEvent): ErrorEvent | null {
  const message = event.exception?.values?.[0]?.value ?? event.message;
  return shouldDropBenignError(message) ? null : event;
}
